import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, Play } from 'lucide-react';

const LOGO = 'https://customer-assets.emergentagent.com/job_agent-connect-93/artifacts/akzw3wcz_goldielocks-logo-2048.png';
const API       = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'nq_token';

const C = {
  bg:         '#111827',
  bgDeep:     '#0C1320',
  surface:    '#19263A',
  surfaceAlt: '#14202E',
  coral:      '#E07A5F',
  gold:       '#C4A87A',
  text:       '#F8F9FA',
  muted:      '#C8D3DF',
  border:     'rgba(255,255,255,0.07)',
};
const serif     = { fontFamily: "'Playfair Display', Georgia, serif" };
const editorial = { fontFamily: "'Cormorant Garamond', 'Cormorant', Georgia, serif" };
const sans      = { fontFamily: "'Outfit', system-ui, sans-serif" };

const HERO_IMG = 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1400&q=80';




// ─── Keyframes injected once ──────────────────────────────────────────────────
const STYLE_TAG = `
  @keyframes gradientDrift {
    0%,100% { background-position: 0% 60%; }
    50%      { background-position: 100% 40%; }
  }
  @keyframes softPulse {
    0%,100% { opacity: 0.55; }
    50%     { opacity: 0.75; }
  }
  @keyframes slowZoom {
    0%   { transform: scale(1.07); }
    100% { transform: scale(1.0); }
  }
  @keyframes goldBreath {
    0%,100% { text-shadow: 0 0 40px rgba(196,168,122,0.10); }
    50%     { text-shadow: 0 0 80px rgba(196,168,122,0.22); }
  }
`;

// ─── Navbar ───────────────────────────────────────────────────────────────────
function Navbar({ onDemo, demoLoading }) {
  return (
    <nav className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 py-3"
      style={{ background: 'rgba(255,255,255,0.97)', backdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(0,0,0,0.08)', boxShadow: '0 1px 12px rgba(0,0,0,0.07)' }}>
      <img src={LOGO} alt="Goldielocks" style={{ height: '44px', width: 'auto', objectFit: 'contain' }} />
      <div className="flex items-center gap-3">
        <button onClick={onDemo} disabled={demoLoading}
          data-testid="nav-demo-btn"
          className="flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-semibold transition-all disabled:opacity-50 hover:opacity-75"
          style={{ ...sans, background: '#F3F4F6', color: '#6B7280', border: '1px solid #E5E7EB' }}>
          {demoLoading ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} fill="currentColor" />}
          Preview
        </button>
        <Link to="/login" data-testid="nav-signin"
          className="px-4 py-1.5 rounded-full text-sm font-semibold transition-all hover:opacity-75"
          style={{ ...sans, color: '#374151', border: '1px solid #D1D5DB' }}>Sign in</Link>
        <Link to="/register" data-testid="nav-signup"
          className="px-4 py-1.5 rounded-full text-sm font-semibold transition-all hover:opacity-90 hover:scale-105"
          style={{ ...sans, background: C.coral, color: '#fff' }}>Get started</Link>
      </div>
    </nav>
  );
}

// ─── Hero ─────────────────────────────────────────────────────────────────────
function Hero({ onDemo, demoLoading, demoError }) {
  return (
    <section className="relative min-h-screen flex items-center justify-start overflow-hidden" style={{ background: C.bg }}>
      <style>{STYLE_TAG}</style>

      {/* Background photo — slow imperceptible zoom */}
      <div className="absolute inset-0">
        <img src={HERO_IMG} alt="" className="w-full h-full object-cover"
          style={{ opacity: 0.35, animation: 'slowZoom 16s cubic-bezier(0.0,0.0,0.2,1) forwards', transformOrigin: 'center center' }}
          onError={e => { e.target.style.opacity = 0; }} />
        <div className="absolute inset-0"
          style={{ background: 'linear-gradient(135deg, rgba(17,24,39,0.96) 0%, rgba(17,24,39,0.58) 60%, rgba(17,24,39,0.85) 100%)' }} />
        {/* Bottom fade into next section */}
        <div className="absolute bottom-0 left-0 right-0 h-40 pointer-events-none"
          style={{ background: `linear-gradient(to bottom, transparent 0%, ${C.bgDeep} 100%)` }} />
      </div>

      {/* Ambient orb */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div style={{
          position: 'absolute', top: '20%', left: '-10%',
          width: '70vw', height: '70vw', maxWidth: '700px', maxHeight: '700px',
          background: 'radial-gradient(ellipse at center, rgba(196,168,122,0.07) 0%, rgba(224,122,95,0.04) 45%, transparent 70%)',
          borderRadius: '50%', filter: 'blur(50px)',
        }} />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-6 pt-40 pb-28">

        {/* Setup line 1 */}
        <motion.p
          initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.7, ease: 'easeOut' }}
          className="leading-snug"
          style={{ ...editorial, color: 'rgba(248,249,250,0.82)', fontSize: 'clamp(1.9rem,4vw,3.2rem)', fontWeight: 300, letterSpacing: '0.01em' }}>
          Not too much.
        </motion.p>

        {/* Setup line 2 */}
        <motion.p
          initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.48, duration: 0.7, ease: 'easeOut' }}
          className="leading-snug mb-10"
          style={{ ...editorial, color: 'rgba(248,249,250,0.82)', fontSize: 'clamp(1.9rem,4vw,3.2rem)', fontWeight: 300, letterSpacing: '0.01em' }}>
          Not too little.
        </motion.p>

        {/* Payoff — scale-in, gold breath */}
        <motion.h1
          initial={{ opacity: 0, scale: 0.98, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ delay: 0.68, duration: 1.0, ease: [0.16, 1, 0.3, 1] }}
          className="mb-20 leading-none"
          style={{
            ...editorial,
            color: C.gold,
            fontStyle: 'italic',
            fontSize: 'clamp(4.3rem,9.5vw,8.6rem)',
            fontWeight: 600,
            letterSpacing: '-0.02em',
            animation: 'goldBreath 10s ease-in-out 1.8s infinite',
          }}>
          Just right.
        </motion.h1>

        {/* Subtext */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.95, duration: 0.7, ease: 'easeOut' }}
          className="mb-14 space-y-0.5">
          <p style={{ ...sans, color: 'rgba(200,211,223,0.82)', fontSize: 'clamp(0.95rem,1.8vw,1.1rem)', lineHeight: 1.9, fontWeight: 400, letterSpacing: '0.01em' }}>
            Not everything.
          </p>
          <p style={{ ...sans, color: 'rgba(248,249,250,0.88)', fontSize: 'clamp(0.95rem,1.8vw,1.1rem)', lineHeight: 1.9, fontWeight: 600, letterSpacing: '0.01em' }}>
            Just what fits.
          </p>
        </motion.div>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.65, ease: 'easeOut' }}
          className="flex flex-wrap items-center gap-6 mb-8">

          <motion.div
            whileHover={{ y: -2, transition: { duration: 0.2, ease: 'easeOut' } }}
            whileTap={{ scale: 0.98 }}>
            <Link to="/register" data-testid="hero-get-started"
              className="inline-flex items-center px-9 py-4 rounded-full font-semibold text-sm transition-all duration-300"
              style={{ ...sans, background: C.coral, color: '#fff', letterSpacing: '0.02em', boxShadow: '0 4px 24px rgba(224,122,95,0.28)' }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 32px rgba(224,122,95,0.42)'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 4px 24px rgba(224,122,95,0.28)'; }}>
              Find my match
            </Link>
          </motion.div>

          <motion.button
            onClick={onDemo} disabled={demoLoading} data-testid="explore-demo-btn"
            whileHover={{ y: -2, transition: { duration: 0.2, ease: 'easeOut' } }}
            className="text-sm font-medium transition-all duration-200 disabled:opacity-40"
            style={{ ...sans, color: 'rgba(200,211,223,0.78)', background: 'none', border: 'none', padding: 0, letterSpacing: '0.02em', textDecoration: 'underline', textUnderlineOffset: '5px', textDecorationColor: 'rgba(200,211,223,0.3)' }}
            onMouseEnter={e => { e.currentTarget.style.color = 'rgba(200,211,223,0.95)'; e.currentTarget.style.textDecorationColor = 'rgba(200,211,223,0.55)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'rgba(200,211,223,0.78)'; e.currentTarget.style.textDecorationColor = 'rgba(200,211,223,0.3)'; }}>
            {demoLoading ? 'Loading…' : 'Just browsing'}
          </motion.button>
        </motion.div>

        {demoError && <p className="text-sm mb-4" style={{ color: '#fc8181' }} data-testid="demo-error">{demoError}</p>}

        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          transition={{ delay: 1.25, duration: 0.8, ease: 'easeOut' }}
          style={{ ...sans, color: 'rgba(200,211,223,0.62)', fontSize: '0.78rem', letterSpacing: '0.03em' }}>
          No account. Just better matches.
        </motion.p>

        {/* Seller secondary CTA */}
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 0.8, ease: 'easeOut' }}
          className="mt-8"
          style={{ ...sans, color: 'rgba(200,211,223,0.45)', fontSize: '0.9rem', letterSpacing: '0.01em' }}>
          Buying or selling?{' '}
          <Link to="/register?role=seller"
            data-testid="hero-seller-link"
            style={{ color: 'rgba(196,168,122,0.85)', fontWeight: 700, textDecoration: 'underline', textUnderlineOffset: '4px', textDecorationColor: 'rgba(196,168,122,0.35)' }}
            onMouseEnter={e => { e.currentTarget.style.color = C.gold; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'rgba(196,168,122,0.85)'; }}>
            List my home
          </Link>
        </motion.p>
      </div>
    </section>
  );
}

// ─── Brand Matching Section ───────────────────────────────────────────────────
const PRINCIPLES = [
  {
    num: '01',
    title: 'You know your type',
    body: "Space to breathe. Light that lands just right. A kitchen you'll actually use. We're listening.",
  },
  {
    num: '02',
    title: 'Homes show up honestly',
    body: 'Not just the good parts. The tradeoffs too. Because the right match holds up to the truth.',
  },
  {
    num: '03',
    title: 'The right ones rise',
    body: "Not everything. Not even most. Just the ones that actually fit — ranked, explained, yours.",
  },
];

function HowItWorks() {
  return (
    <section style={{ background: C.surface, padding: '9rem 0' }}>
      <div style={{ maxWidth: '1160px', margin: '0 auto', padding: '0 3rem' }}>

        {/* Top: split headline + subtext */}
        <div className="flex flex-col md:flex-row md:items-end gap-8 md:gap-16"
          style={{ marginBottom: '5rem' }}>
          <motion.h2
            initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.85, ease: [0.16, 1, 0.3, 1] }}
            style={{ ...editorial, color: C.text, fontSize: 'clamp(3rem,6vw,5.5rem)', fontWeight: 600, letterSpacing: '-0.03em', lineHeight: 0.98, flex: '1 1 auto' }}>
            Some things<br />just click.
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }}
            viewport={{ once: true }} transition={{ delay: 0.25, duration: 0.7 }}
            style={{ ...sans, color: 'rgba(200,211,223,0.78)', fontSize: '1.05rem', lineHeight: 1.85, maxWidth: '300px', flex: '0 0 auto', paddingBottom: '0.4rem' }}>
            This is one of them.
          </motion.p>
        </div>

        {/* Three principles as editorial rows */}
        <div>
          {PRINCIPLES.map((p, i) => (
            <motion.div key={i}
              initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.14, duration: 0.75, ease: [0.16, 1, 0.3, 1] }}
              className="flex flex-col md:flex-row md:items-baseline gap-4 md:gap-12"
              style={{ padding: '2.4rem 0', borderTop: `1px solid ${C.border}` }}>
              <span style={{ ...sans, color: C.gold, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.2em', opacity: 0.65, width: '36px', flexShrink: 0 }}>
                {p.num}
              </span>
              <h3 style={{ ...serif, color: C.text, fontSize: 'clamp(1.3rem,2.2vw,1.8rem)', fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1.15, flex: '0 0 260px' }}>
                {p.title}
              </h3>
              <p style={{ ...sans, color: 'rgba(200,211,223,0.78)', fontSize: '0.92rem', lineHeight: 1.9, letterSpacing: '0.005em', maxWidth: '480px' }}>
                {p.body}
              </p>
            </motion.div>
          ))}
          <div style={{ height: '1px', background: C.border }} />
        </div>

        {/* Closing pull-quote */}
        <motion.p
          initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ delay: 0.3, duration: 0.8 }}
          style={{ ...editorial, color: C.gold, fontSize: 'clamp(1.3rem,2.4vw,2rem)', fontStyle: 'italic', fontWeight: 400, letterSpacing: '-0.01em', opacity: 0.8, textAlign: 'right', marginTop: '3.5rem' }}>
          That's the difference.
        </motion.p>
      </div>
    </section>
  );
}

// ─── Billboard Moment ─────────────────────────────────────────────────────────
function BrandMoment() {
  return (
    <section style={{ background: '#070C16', padding: '10rem 3rem', overflow: 'hidden' }}>
      <div style={{ maxWidth: '1160px', margin: '0 auto' }}>

        <motion.h2
          initial={{ opacity: 0, y: 36 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 1.0, ease: [0.16, 1, 0.3, 1] }}
          style={{ ...editorial, color: C.text, fontSize: 'clamp(3.8rem,8.5vw,9.5rem)', fontWeight: 600, letterSpacing: '-0.04em', lineHeight: 0.94 }}>
          Not every home<br />is your type.
        </motion.h2>

        <motion.div
          initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }}
          viewport={{ once: true }} transition={{ delay: 0.4, duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          style={{ height: '1px', background: `linear-gradient(90deg, ${C.gold}, transparent)`, transformOrigin: 'left', marginTop: '3rem', marginBottom: '3rem', maxWidth: '600px', opacity: 0.4 }} />

        <motion.p
          initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ delay: 0.55, duration: 0.75 }}
          style={{ ...editorial, color: C.gold, fontSize: 'clamp(1.4rem,2.8vw,2.4rem)', fontStyle: 'italic', fontWeight: 400, opacity: 0.88 }}>
          And that's the point.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ delay: 0.75, duration: 0.7 }}
          className="flex flex-col gap-1.5"
          style={{ marginTop: '3.5rem', maxWidth: '420px' }}>
          {[
            { t: 'Some have the specs.',     dim: true  },
            { t: 'Some check the boxes.',    dim: true  },
            { t: 'A few just feel right.',   dim: false },
          ].map((item, i) => (
            <p key={i} style={{ ...sans, color: item.dim ? 'rgba(200,211,223,0.62)' : 'rgba(248,249,250,0.88)', fontSize: item.dim ? '0.97rem' : '1.1rem', fontWeight: item.dim ? 400 : 600, lineHeight: 1.8, letterSpacing: item.dim ? 0 : '-0.01em' }}>
              {item.t}
            </p>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// ─── Seller CTA Section ───────────────────────────────────────────────────────
function SellerCTA() {
  return (
    <section style={{ background: '#0A192F', padding: '9rem 3rem', position: 'relative', overflow: 'hidden' }}>
      {/* Subtle gold orb */}
      <div className="absolute pointer-events-none"
        style={{ top: '10%', right: '-5%', width: '40vw', height: '40vw', maxWidth: '480px', maxHeight: '480px',
          background: 'radial-gradient(ellipse at center, rgba(196,168,122,0.08) 0%, transparent 65%)',
          borderRadius: '50%', filter: 'blur(40px)' }} />

      <div style={{ maxWidth: '1160px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
        <div className="flex flex-col md:flex-row md:items-center gap-12 md:gap-20">

          {/* Left */}
          <div style={{ flex: '1 1 auto' }}>
            <motion.p
              initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.6 }}
              style={{ ...sans, color: 'rgba(196,168,122,0.6)', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: '1.2rem' }}>
              For homeowners
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: 0.1, duration: 0.85, ease: [0.16, 1, 0.3, 1] }}
              style={{ ...editorial, color: C.text, fontSize: 'clamp(2.8rem,5.5vw,5rem)', fontWeight: 600, letterSpacing: '-0.03em', lineHeight: 1.0, marginBottom: '1.5rem' }}>
              Have a home that's<br />
              <span style={{ color: C.gold, fontStyle: 'italic' }}>just right for someone?</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0 }} whileInView={{ opacity: 1 }}
              viewport={{ once: true }} transition={{ delay: 0.3, duration: 0.7 }}
              style={{ ...sans, color: 'rgba(200,211,223,0.7)', fontSize: '1.02rem', lineHeight: 1.9, maxWidth: '440px' }}>
              We'll match it with buyers already looking for it.<br />
              No open houses. No wasted showings. Just the right people.
            </motion.p>
          </div>

          {/* Right */}
          <motion.div
            initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ delay: 0.35, duration: 0.65 }}
            style={{ flex: '0 0 auto' }}>
            <motion.div whileHover={{ y: -2, transition: { duration: 0.2 } }} whileTap={{ scale: 0.98 }}>
              <Link to="/register?role=seller" data-testid="seller-cta-btn"
                className="inline-flex items-center px-9 py-4 rounded-full font-bold text-base transition-all duration-300"
                style={{ ...sans, background: C.gold, color: '#0A192F', letterSpacing: '0.04em', boxShadow: '0 4px 28px rgba(196,168,122,0.25)' }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 36px rgba(196,168,122,0.38)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 4px 28px rgba(196,168,122,0.25)'; }}>
                List my home
              </Link>
            </motion.div>
            <p className="mt-4 text-xs" style={{ ...sans, color: 'rgba(200,211,223,0.38)', lineHeight: 1.75 }}>
              No listing fees until you close.<br />Matched buyers, not browsing traffic.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
function FinalCTA({ onDemo, demoLoading }) {
  return (
    <section style={{ background: C.surfaceAlt, padding: '9rem 3rem' }}>
      <div style={{ maxWidth: '1160px', margin: '0 auto' }}>
        <div className="flex flex-col md:flex-row md:items-end gap-12 md:gap-20">

          {/* Left: headline */}
          <div style={{ flex: '1 1 auto' }}>
            <motion.h2
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.85, ease: [0.16, 1, 0.3, 1] }}
              style={{ ...editorial, color: C.text, fontSize: 'clamp(2.8rem,5.5vw,5rem)', fontWeight: 600, letterSpacing: '-0.03em', lineHeight: 1.0 }}>
              Ready to find<br />
              <span style={{ color: C.gold, fontStyle: 'italic' }}>your match?</span>
            </motion.h2>
          </div>

          {/* Right: copy + CTAs */}
          <div style={{ flex: '0 0 auto', maxWidth: '360px' }}>
            <motion.div
              initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: 0.2, duration: 0.7 }}
              style={{ marginBottom: '2.5rem' }}>
              <p style={{ ...sans, color: 'rgba(200,211,223,0.78)', fontSize: '0.97rem', lineHeight: 2.0 }}>You've been browsing.</p>
              <p style={{ ...sans, color: 'rgba(200,211,223,0.78)', fontSize: '0.97rem', lineHeight: 2.0 }}>We've been paying attention.</p>
              <p style={{ ...sans, color: 'rgba(248,249,250,0.85)', fontSize: '1rem', lineHeight: 2.0, fontWeight: 500 }}>Let's find the one.</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: 0.35, duration: 0.65 }}
              className="flex flex-wrap items-center gap-5">
              <motion.div whileHover={{ y: -2, transition: { duration: 0.2 } }} whileTap={{ scale: 0.98 }}>
                <Link to="/register" data-testid="final-cta-signup"
                  className="inline-flex items-center px-9 py-4 rounded-full font-semibold text-sm transition-all duration-300"
                  style={{ ...sans, background: C.coral, color: '#fff', letterSpacing: '0.02em', boxShadow: '0 4px 28px rgba(224,122,95,0.28)' }}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 36px rgba(224,122,95,0.42)'; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 4px 28px rgba(224,122,95,0.28)'; }}>
                  Find my match
                </Link>
              </motion.div>
              <button onClick={onDemo} disabled={demoLoading}
                data-testid="final-browse-btn"
                className="text-sm font-medium transition-all duration-200 disabled:opacity-40"
                style={{ ...sans, color: 'rgba(200,211,223,0.72)', background: 'none', border: 'none', padding: 0, letterSpacing: '0.02em', textDecoration: 'underline', textUnderlineOffset: '5px', textDecorationColor: 'rgba(200,211,223,0.3)' }}
                onMouseEnter={e => { e.currentTarget.style.color = 'rgba(200,211,223,0.95)'; }}
                onMouseLeave={e => { e.currentTarget.style.color = 'rgba(200,211,223,0.72)'; }}>
                {demoLoading ? <><Loader2 size={14} className="inline animate-spin mr-1.5" />Loading…</> : 'Keep browsing'}
              </button>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Footer ───────────────────────────────────────────────────────────────────
function Footer() {
  return (
    <footer className="py-10 px-6 text-center" style={{ background: C.bgDeep, borderTop: `1px solid ${C.border}` }}>
      <p style={{ ...sans, color: 'rgba(200,211,223,0.62)', fontSize: '0.78rem' }}>
        © 2026 Goldielocks ·{' '}
        <Link to="/login" className="hover:opacity-70 transition-opacity" style={{ color: 'rgba(200,211,223,0.62)' }}>Sign in</Link>
        {' · '}
        <Link to="/register" className="hover:opacity-70 transition-opacity" style={{ color: 'rgba(200,211,223,0.62)' }}>Get started</Link>
      </p>
    </footer>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function Landing() {
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoError,   setDemoError]   = useState('');

  const handleExploreDemo = async () => {
    setDemoLoading(true);
    setDemoError('');
    try {
      if (!API) throw new Error('Backend URL not configured.');
      const res         = await fetch(`${API}/api/demo/access`);
      const contentType = res.headers.get('content-type') || '';
      if (!contentType.includes('application/json'))
        throw new Error('Backend unreachable — check REACT_APP_BACKEND_URL in your Netlify env vars.');
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Demo unavailable');
      localStorage.setItem(TOKEN_KEY, data.access_token);
      window.location.href = '/discover';
    } catch (e) {
      setDemoError(e.message || 'Could not start demo. Please try again.');
      setDemoLoading(false);
    }
  };

  return (
    <div style={{ background: C.bg, minHeight: '100vh', ...sans }}>
      <Navbar   onDemo={handleExploreDemo} demoLoading={demoLoading} />
      <Hero     onDemo={handleExploreDemo} demoLoading={demoLoading} demoError={demoError} />
      <HowItWorks />
      <BrandMoment />
      <SellerCTA />
      <FinalCTA onDemo={handleExploreDemo} demoLoading={demoLoading} />
      <Footer />
    </div>
  );
}
